import { API_BASE_URL } from "@/config";
import { validateToken } from '@/utils/auth';
import { useToast } from "@/components/Toast/useToast";

export default {
  name: 'RegisterQuestionSelection',
  setup() {
    const { showSuccess, showError } = useToast();
    
    return {
      showSuccess,
      showError
    };
  },
  data: () => ({
    loading: false,
    service_id: null,
    service_name: '',
    service_types: [],
    selected_service_type: 'all',
    all_questions: [],
    filtered_questions: [],
    selected_questions: [],
    already_assigned_questions: [],
    search_query: ''
  }),
  computed: {
    all_questions_selected() {
      return this.filtered_questions.length > 0 && 
             this.filtered_questions.every(q => this.selected_questions.includes(q.id));
    }
  },
  methods: {
    async fetch_service_types() {
      const TOKEN = localStorage.getItem('token');
      const GET_SERVICE_TYPES_API = `${API_BASE_URL}/verify_api/form_management_api/get_service_types/`;
      
      try {
        const response = await new Promise((resolve, reject) => {
          $.ajax({
            url: GET_SERVICE_TYPES_API,
            method: 'GET',
            headers: {
              Authorization: `Token ${TOKEN}`
            },
            success: resolve,
            error: reject
          });
        });

        this.service_types = response.service_types || [];
      } catch (error) {
        this.showError('Error', 'Failed to fetch service types');
      }
    },

    async fetch_service_info() {
      const TOKEN = localStorage.getItem('token');
      const GET_SERVICE_API = `${API_BASE_URL}/verify_api/form_management_api/get_questions_for_service_api/${this.service_id}`;
      
      try {
        const response = await new Promise((resolve, reject) => {
          $.ajax({
            url: GET_SERVICE_API,
            method: 'GET',
            headers: {
              Authorization: `Token ${TOKEN}`
            },
            success: resolve,
            error: reject
          });
        });

        this.service_name = response.service_name || 'Register Form';
        
        // Store already assigned questions
        if (response.questions && response.questions.length > 0) {
          this.already_assigned_questions = response.questions.map(q => q.id);
          this.selected_questions = [...this.already_assigned_questions];
        }
      } catch (error) {
        console.error('Error fetching service info:', error);
      }
    },

    async fetch_service_type_questions() {
      const TOKEN = localStorage.getItem('token');
      let GET_QUESTIONS_API;
      
      if (this.selected_service_type === 'all' || !this.selected_service_type) {
        // Fetch all active system questions
        GET_QUESTIONS_API = `${API_BASE_URL}/verify_api/question_management_api/get_all_system_questions/`;
      } else {
        // Fetch questions for specific service type
        GET_QUESTIONS_API = `${API_BASE_URL}/verify_api/question_management_api/get_questions_by_service_type/${this.selected_service_type}`;
      }
      
      this.loading = true;
      
      try {
        const response = await new Promise((resolve, reject) => {
          $.ajax({
            url: GET_QUESTIONS_API,
            method: 'GET',
            headers: {
              Authorization: `Token ${TOKEN}`
            },
            success: resolve,
            error: reject
          });
        });

        this.all_questions = response.questions || [];
        this.filter_questions();
      } catch (error) {
        this.showError('Error', 'Failed to fetch questions');
        this.all_questions = [];
        this.filtered_questions = [];
      } finally {
        this.loading = false;
      }
    },

    filter_questions() {
      let questions = [...this.all_questions];
      
      // Filter by search query
      if (this.search_query) {
        const query = this.search_query.toLowerCase();
        questions = questions.filter(q => 
          q.question.toLowerCase().includes(query) ||
          (q.question_type && q.question_type.toLowerCase().includes(query))
        );
      }
      
      this.filtered_questions = questions;
    },

    toggle_all_questions() {
      if (this.all_questions_selected) {
        // Deselect all from current view
        this.filtered_questions.forEach(q => {
          const index = this.selected_questions.indexOf(q.id);
          if (index > -1) {
            this.selected_questions.splice(index, 1);
          }
        });
      } else {
        // Select all from current view
        this.filtered_questions.forEach(q => {
          if (!this.selected_questions.includes(q.id)) {
            this.selected_questions.push(q.id);
          }
        });
      }
    },

    async save_questions() {
      const TOKEN = localStorage.getItem('token');
      const ASSIGN_QUESTIONS_API = `${API_BASE_URL}/verify_api/form_management_api/assign_questions_to_register/`;
      
      this.loading = true;
      
      try {
        const response = await new Promise((resolve, reject) => {
          $.ajax({
            url: ASSIGN_QUESTIONS_API,
            method: 'POST',
            contentType: 'application/json',
            headers: {
              Authorization: `Token ${TOKEN}`
            },
            data: JSON.stringify({
              service_id: this.service_id,
              question_ids: this.selected_questions
            }),
            success: resolve,
            error: reject
          });
        });

        if (response.status === 'success') {
          this.showSuccess('Success', response.message || 'Questions saved successfully');
          
          // Go back after a short delay
          setTimeout(() => {
            this.go_back();
          }, 1500);
        }
      } catch (error) {
        let error_message = 'Failed to save questions';
        if (error.responseJSON && error.responseJSON.message) {
          error_message = error.responseJSON.message;
        }
        this.showError('Error', error_message);
      } finally {
        this.loading = false;
      }
    },

    go_back() {
      this.$router.push({ name: 'workflow_services' });
    }
  },
  async mounted() {
    if (!validateToken(this.$router)) {
      return;
    }

    this.service_id = this.$route.params.service_id;
    
    if (!this.service_id) {
      this.showError('Error', 'Service ID is required');
      this.go_back();
      return;
    }

    this.loading = true;
    await this.fetch_service_types();
    await this.fetch_service_info();
    await this.fetch_service_type_questions();
    this.loading = false;
  }
};
